import {
  Form,
  FormField,
  FormInput,
  FormPrefixedInput,
  Button,
  FormOTPInput,
} from "web-design-system";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import ThrottledButton from "@core/components/throttledButton";
import { useDistributorAuthActions } from "creso-core/hooks";
import { router } from "@/App";
import { SuccessLottieAnimation } from "web-core/components";
import { ArrowLeftIcon } from "lucide-react";
import { APIError } from "web-core/utils";
import { arnSchema } from "creso-core/zod";
import { AuthSplitLayout } from "../components/auth-split-layout";

const arnOtpSchema = z
  .object({
    arn_code: arnSchema,
    otp: z.string().length(6, "OTP must be exactly 6 digits"),
  })
  .refine((data) => !data.otp || data.otp.length === 6, {
    message: "OTP must be 6 digits",
    path: ["otp"],
  });

const passwordSchema = z
  .object({
    password: z.string().min(1, "Password is required"),
    confirmPassword: z.string().min(1, "Confirm Password is required"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

enum ResetPasswordStep {
  REQUEST_OTP = 1,
  ENTER_OTP = 2,
  CREATE_PASSWORD = 3,
  SUCCESS = 4,
}

export default function ResetPassword() {
  const [step, setStep] = useState<ResetPasswordStep>(
    ResetPasswordStep.REQUEST_OTP
  );
  const { login, requestOtp, setNewPassword } = useDistributorAuthActions();

  const form_one = useForm<z.infer<typeof arnOtpSchema>>({
    resolver: zodResolver(arnOtpSchema),
    defaultValues: {
      arn_code: "",
      otp: "",
    },
  });

  const form_two = useForm<z.infer<typeof passwordSchema>>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const { trigger, getValues, handleSubmit: handleOtpSubmit } = form_one;
  const { handleSubmit: handlePasswordSubmit } = form_two;

  const sendOtp = async () => {
    if (await trigger("arn_code")) {
      const arn = getValues("arn_code");
      requestOtp(arn, {
        onSuccess: () => {
          form_one.clearErrors("arn_code");
          setStep(ResetPasswordStep.ENTER_OTP);
        },
        onError: (error) => {
          const message =
            error instanceof APIError
              ? error.data.message
              : "Something went wrong, please try again.";
          form_one.setError("arn_code", { message });
        },
      });
    }
  };

  const onSubmitOtp = async (data: z.infer<typeof arnOtpSchema>) => {
    try {
      if (!data.otp) return;

      await login(data.arn_code, data.otp);
      setStep(ResetPasswordStep.CREATE_PASSWORD);
      form_one.resetField("otp");
    } catch (error) {
      if (!(error instanceof APIError)) {
        form_one.setError("otp", {
          message: "Something went wrong, please try again.",
        });
        return;
      }

      const code = (error.data?.code ?? "").toUpperCase();
      const details =
        error.data?.details && typeof error.data.details === "object"
          ? (error.data.details as Record<string, unknown>)
          : undefined;
      const detailMessage = (key: string): string | undefined => {
        const value = details?.[key];
        if (typeof value === "string" && value.trim().length > 0) {
          return value.trim();
        }
        if (
          Array.isArray(value) &&
          value.length > 0 &&
          typeof value[0] === "string"
        ) {
          const first = (value[0] as string).trim();
          return first.length > 0 ? first : undefined;
        }
        return undefined;
      };

      const apiMessage =
        error.data?.message ||
        error.message ||
        "Something went wrong, please try again.";

      const enteredArn = data.arn_code.trim().toLowerCase();
      const normalizeLoginIdentifier = (value?: string) =>
        (value ?? "").trim().replace(/^ARN-/i, "").toLowerCase();
      const usernameDetail =
        detailMessage("arn_code") === enteredArn
          ? undefined
          : detailMessage("arn_code") || detailMessage("username");
      const isUsernameEcho =
        Boolean(usernameDetail) &&
        normalizeLoginIdentifier(usernameDetail) ===
          normalizeLoginIdentifier(data.arn_code);

      if (
        code.includes("USERNAME") ||
        code.includes("ARN") ||
        code.includes("USER_NOT_FOUND") ||
        code.includes("DISTRIBUTOR_NOT_FOUND") ||
        code.includes("EMPLOYEE_NOT_FOUND")
      ) {
        form_one.setError("arn_code", {
          message: isUsernameEcho ? apiMessage : usernameDetail || apiMessage,
        });
        return;
      }

      const otpMessage =
        detailMessage("password_or_otp") ||
        detailMessage("otp") ||
        detailMessage("non_field_errors");

      form_one.setError("otp", {
        message:
          otpMessage ||
          (apiMessage.toLowerCase().includes("unknown error")
            ? "Incorrect username or OTP"
            : apiMessage),
      });
    }
  };

  const onSubmitPassword = async (data: z.infer<typeof passwordSchema>) => {
    if (data.password && data.password === data.confirmPassword) {
      try {
        await setNewPassword(form_one.getValues("arn_code"), data.password);
        setStep(ResetPasswordStep.SUCCESS);
      } catch {
        form_two.setError("password", {
          message: "Password reset failed, please try again.",
        });
      }
    } else {
      form_two.setError("confirmPassword", {
        message: "Passwords do not match",
      });
    }
  };

  const HandleRedirectLogin = () => {
    router.navigate({
      to: "/",
    });
  };

  return (
    <AuthSplitLayout>
      <div className="flex flex-col w-full h-full gap-6">
        <div className="flex items-center gap-3">
          <ArrowLeftIcon
            onClick={() => router.navigate({ to: "/login/distributor" })}
            className="size-5 hover:scale-110 transition-all duration-300"
          />
          {step !== ResetPasswordStep.SUCCESS && (
            <span className="text-2xl font-semibold">Reset Password</span>
          )}
        </div>

        {step === ResetPasswordStep.REQUEST_OTP && (
          <Form {...form_one}>
            <form className="flex-1 h-full flex flex-col gap-6 pt-4">
              <FormField
                control={form_one.control}
                name="arn_code"
                render={({ field }) => (
                  <FormPrefixedInput
                    {...field}
                    onChange={(value) => {
                      const sanitizedValue = String(value).replace(
                        /[^a-zA-Z0-9._-]/g,
                        ""
                      );
                      field.onChange(sanitizedValue);
                    }}
                    label="Username"
                    type="text"
                    autoComplete="username"
                    prefix=""
                    className="gap-2"
                  >
                    ARN
                  </FormPrefixedInput>
                )}
              />
              <Button
                type="button"
                size="lg"
                onClick={sendOtp}
                className="w-full mt-5"
              >
                Request OTP
              </Button>
            </form>
          </Form>
        )}

        {step === ResetPasswordStep.ENTER_OTP && (
          <Form {...form_one}>
            <form
              onSubmit={handleOtpSubmit(onSubmitOtp)}
              className="flex-1 flex flex-col gap-5"
            >
              <div className="flex flex-col gap-6 pb-8">
                <FormField
                  control={form_one.control}
                  name="arn_code"
                  render={({ field }) => (
                    <FormPrefixedInput
                      {...field}
                      onChange={(value) => {
                        const sanitizedValue = String(value).replace(
                          /[^a-zA-Z0-9._-]/g,
                          ""
                        );
                        field.onChange(sanitizedValue);
                      }}
                      label="Username"
                      type="text"
                      autoComplete="username"
                      prefix=""
                      disabled
                    >
                      ARN
                    </FormPrefixedInput>
                  )}
                />
                <FormField
                  control={form_one.control}
                  name="otp"
                  render={({ field }) => (
                    <div className="flex flex-col gap-5 relative">
                      <FormOTPInput
                        {...field}
                        maxLength={6}
                        label="OTP"
                        description="OTP sent!"
                      />
                      <ThrottledButton
                        throttle={5}
                        variant="link"
                        className="text-xs h-auto px-0 text-right absolute top-0 right-0"
                        size="sm"
                        onClick={sendOtp}
                      >
                        Resend OTP
                      </ThrottledButton>
                    </div>
                  )}
                />
              </div>
              <Button size="lg" className="w-full" type="submit">
                Submit
              </Button>
            </form>
          </Form>
        )}

        {step === ResetPasswordStep.CREATE_PASSWORD && (
          <Form {...form_two}>
            <form
              onSubmit={handlePasswordSubmit(onSubmitPassword)}
              className="flex-1 flex flex-col gap-5"
            >
              <div className="flex flex-col gap-6 pb-8">
                <FormField
                  control={form_two.control}
                  name="password"
                  render={({ field }) => (
                    <FormInput {...field} label="Password" type="password" />
                  )}
                />
                <FormField
                  control={form_two.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      label="Confirm Password"
                      type="password"
                    />
                  )}
                />
              </div>
              <Button className="w-full" size="lg" type="submit">
                Confirm
              </Button>
            </form>
          </Form>
        )}

        {step === ResetPasswordStep.SUCCESS && (
          <div className="flex-1 flex flex-col justify-between">
            <div className="flex-1 flex items-center flex-col justify-center">
              <SuccessLottieAnimation loop={false} height={100} width={100} />
              <h6>Password updated</h6>
            </div>
            <Button className="w-full" size="lg" onClick={HandleRedirectLogin}>
              Enter dashboard
            </Button>
          </div>
        )}
      </div>
    </AuthSplitLayout>
  );
}
